export const isUndefinedTypeGuard = (value: unknown): value is undefined =>
    value === undefined;

export const isNotUndefinedTypeGuard = <T>(
    value: T,
): value is Exclude<T, undefined> => value !== undefined;

export const isNotNullTypeGuard = <T>(value: T): value is Exclude<T, null> =>
    value !== null;

export const throwIfUndefined = <T>(value: T): Exclude<T, undefined> => {
    if (!isNotUndefinedTypeGuard(value)) {
        throw new Error("Value is undefined");
    }
    return value;
};

export const throwIfNull = <T>(value: T): Exclude<T, null> => {
    if (!isNotNullTypeGuard(value)) {
        throw new Error("Value is null");
    }
    return value;
};

export type TFalsy = false | 0 | 0n | "" | null | undefined;
export type TTruthy<T> = Exclude<T, TFalsy>;

export const isFalsy = <T>(value: T): value is Extract<T, TFalsy> => {
    return (
        value === false ||
        Object.is(value, 0) ||
        Object.is(value, -0) ||
        value === 0n ||
        value === "" ||
        value === null ||
        value === undefined ||
        Number.isNaN(value)
    );
};

export const isNullOrUndefined = <T>(
    value: T | null | undefined,
): value is null | undefined => {
    return value === null || value === undefined;
};

export const isTruthy = <T>(value: T): value is TTruthy<T> => {
    return !isFalsy(value);
};

export const isFalsyNonZero = <T>(value: T): value is Extract<T, TFalsy> => {
    return (
        value === false ||
        value === "" ||
        value === null ||
        value === undefined ||
        Number.isNaN(value)
    );
};

export const isTruthyOrZero = <T>(value: T): value is TTruthy<T> => {
    return !isFalsyNonZero(value);
};

export const isString = (value: unknown): value is string => {
    return typeof value === "string";
};

export const isNotNullable = <T>(value: T): value is NonNullable<T> => {
    return value !== null && value !== undefined;
};

export const isNullable = <T>(
    value: T | null | undefined,
): value is null | undefined => {
    return value === null || value === undefined;
};

export function assertNever(x: never): never {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`Unexpected type: ${x}`);
}
